import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSupabase } from '../provider';

// =====================================================
// TYPES
// =====================================================

export interface InviteUserParams {
  email: string;
  role: string;
  location_id: string;
  team_lead_id?: string;
  full_name?: string;
}

export interface InviteResponse {
  success: boolean;
  message: string;
  user_id: string;
}

export interface AppUserWithLocation {
  id: string;
  email: string;
  full_name: string;
  role: 'Ops Manager' | 'Team Lead' | 'Program Staff' | 'Finance' | 'Exec' | 'Trainee' | 'Admin';
  location_id: string | null;
  phone: string | null;
  avatar_url: string | null;
  active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  location?: {
    id: string;
    location_name: string;
  } | null;
}

// =====================================================
// USER LIST HOOK
// =====================================================

export function useUsers(options?: { 
  active?: boolean; 
  role?: string; 
  location_id?: string;
}) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: ['users', 'list', options],
    queryFn: async (): Promise<AppUserWithLocation[]> => {
      let query = supabase
        .from('app_users')
        .select(`
          *,
          location:locations(id, location_name)
        `)
        .order('created_at', { ascending: false });

      if (options?.active !== undefined) {
        query = query.eq('active', options.active);
      }
      if (options?.role && options.role.trim() !== '') {
        query = query.eq('role', options.role);
      }
      if (options?.location_id && options.location_id.trim() !== '') {
        query = query.eq('location_id', options.location_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as AppUserWithLocation[];
    },
  });
}

// =====================================================
// INVITE USER HOOK
// =====================================================

export function useInviteUser() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: InviteUserParams): Promise<InviteResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('invite-user', {
        body: params,
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to send invitation');
      }

      return response.data as InviteResponse;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to invite user: ${error.message}`);
    },
  });
}

// =====================================================
// UPDATE USER HOOK
// =====================================================

export interface UpdateUserParams {
  id: string;
  role?: 'Ops Manager' | 'Team Lead' | 'Program Staff' | 'Finance' | 'Exec' | 'Trainee' | 'Admin';
  location_id?: string | null;
  full_name?: string;
  active?: boolean;
}

export function useUpdateUser() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateUserParams) => {
      const { data, error } = await supabase
        .from('app_users')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });
}

// =====================================================
// DEACTIVATE USER HOOK
// =====================================================

export function useDeactivateUser() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase
        .from('app_users')
        .update({ active: false } as never)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('User deactivated');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to deactivate user: ${error.message}`);
    },
  });
}

// =====================================================
// TEAM LEADS HOOK (for assigning users to team leads)
// =====================================================

interface TeamLeadWithUser {
  id: string;
  user_id: string;
  location_id: string;
  hire_date: string | null;
  certifications: string[] | null;
  specializations: string[] | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  } | null;
}

export function useTeamLeads(locationId?: string) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: ['team_leads', 'list', locationId],
    queryFn: async (): Promise<TeamLeadWithUser[]> => {
      let query = supabase
        .from('team_leads')
        .select(`
          *,
          app_user:app_users(id, full_name, email)
        `)
        .eq('active', true);

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as any;
    },
  });
}
